import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/api/client";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ assets: [], defects: [], claims: [] });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults({ assets: [], defects: [], claims: [] }); return; }
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => { setResults(r.data); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };
  const total = results.assets.length + results.defects.length + results.claims.length;

  return (
    <div ref={ref} className="relative w-80">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        data-testid="global-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q.length >= 2 && setOpen(true)}
        placeholder="Rechercher un actif, désordre, sinistre..."
        className="w-full h-9 pl-9 pr-3 text-sm rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
      />
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50" data-testid="search-results">
          {total === 0 && <div className="p-3 text-xs text-slate-400">Aucun résultat</div>}
          {results.assets.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50">Actifs ({results.assets.length})</div>
              {results.assets.map((a) => (
                <button key={a.id} data-testid={`search-asset-${a.id}`} onClick={() => go(`/assets/${a.id}`)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100">
                  <div className="font-semibold text-slate-900">{a.name}</div>
                  <div className="text-xs text-slate-500">{a.city} • {a.type}</div>
                </button>
              ))}
            </div>
          )}
          {results.defects.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50">Désordres ({results.defects.length})</div>
              {results.defects.map((d) => (
                <button key={d.id} data-testid={`search-defect-${d.id}`} onClick={() => go("/defects")} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100">
                  <div className="font-mono text-[10px] text-slate-400">{d.reference}</div>
                  <div className="font-semibold text-slate-900">{d.title}</div>
                </button>
              ))}
            </div>
          )}
          {results.claims.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-50">Sinistres ({results.claims.length})</div>
              {results.claims.map((c) => (
                <button key={c.id} data-testid={`search-claim-${c.id}`} onClick={() => go("/claims")} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100">
                  <div className="font-mono text-[10px] text-slate-400">{c.reference}</div>
                  <div className="font-semibold text-slate-900">{c.title}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
